import type { ChatHandoff } from "@utils/onboarding/chatHandoffReference";
import { peekPendingOverlapContext } from "./overlapChatContext";

/**
 * 广告入口打开的重叠 Chat：用户从 Chat 返回要落 Brief 首页，而不是回到登录页（EDG-561 AC-4.14）。
 *
 * Web 里 `returnToBrief` 只透传给原生，浏览器内无效；资料包靠 `chatExitBrief` 存储键实现
 * 「Chat 返回 → Brief」，这里复用同一个键与同一退出分支，只泛化「何时写键」。
 *
 * 判定读 `peekPendingOverlapContext()`（不消费）：它在**结果曝光**（`useOverlapMessage`）才被消费，
 * 所以 Chat 挂载时仍能读到 `entry_source: "ad"`。
 */
export const isEtfOverlapAdChat = (handoff: ChatHandoff | null): boolean =>
  handoff?.kind === "etf_overlap" &&
  peekPendingOverlapContext()?.entry_source === "ad";

// 退出时要上报 `overlap_chat_exit{ has_result }`：哪个 chat 是广告重叠 chat、里面出没出过结果，
// 都发生在 Chat 容器之外，用模块级小表传递（不落盘、不新增 store）。
const adChatIds = new Set<string>();
const chatIdsWithResult = new Set<string>();

export const rememberOverlapAdChat = (chatId: string): void => {
  adChatIds.add(chatId);
};

export const noteOverlapResult = (chatId: string): void => {
  chatIdsWithResult.add(chatId);
};

/** 退出时取走：不是广告重叠 chat 返回 `null`；取走后清理，同一 chat 只上报一次。 */
export const takeOverlapAdChatExit = (
  chatId?: string | null,
): { hasResult: boolean } | null => {
  if (!chatId || !adChatIds.has(chatId)) {
    return null;
  }
  const hasResult = chatIdsWithResult.has(chatId);
  adChatIds.delete(chatId);
  chatIdsWithResult.delete(chatId);
  return { hasResult };
};

/** 仅供单测重置模块状态。 */
export const resetOverlapAdChatForTest = (): void => {
  adChatIds.clear();
  chatIdsWithResult.clear();
};
